import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarDays, Clock3, Copy, ExternalLink, Link as LinkIcon, Mail, MapPin, MessageCircle, PartyPopper, RotateCcw, Smartphone, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { LocationAutocomplete } from '@/components/LocationAutocomplete';
import { SubtypePicker } from '@/components/celebrations/SubtypePicker';
import { TemplatePicker } from '@/components/celebrations/TemplatePicker';
import { TypeGalleryPicker } from '@/components/celebrations/TypeGalleryPicker';
import { getBestMatchTemplate } from '@/lib/celebrations/celebrationTemplateMatching';
import { useCelebrationTemplates } from '@/hooks/useCelebrationTemplates';
import { getLabelForType, getSubtypesForType, getTypeByKey } from '@/config/celebrationRegistry';
import { celebrationService, type PublicQuickCreateCelebrationDto, type QuickCreateCelebrationResponse } from '@/lib/api/celebration.service';
import { handleApiError } from '@/lib/api/client';
import { TEMPLATES } from '@/lib/landing-theme-constants';
import { clearCrossModeDraft, readCrossModeDraft, writeCrossModeDraft } from '@/lib/celebrations/crossModeDraft';
import { CelebrantsEditor } from '@/components/celebrations/CelebrantsEditor';
import type { CelebrationPromotionReadiness, LandingThemeConfig } from '@/types/celebration.types';
import type { CelebrationDraftDto, CelebrationFeatureProfile, CelebrantEntry } from '@om/shared-types';
import * as celebrantConfig from '@om/shared-types/celebrant-config';
import * as eventListConfig from '@om/shared-types/event-list';
import { eventService } from '@/lib/api/event.service';
const { getMinCelebrantsForType, getCelebrantRoleOptions, getPredictedRoleAt, toCelebrantArrays } = celebrantConfig;
import { useAuth } from '@/hooks/useAuth';

const todayDate = (): string => new Date().toISOString().slice(0, 10);
const QUICK_DRAFT_KEY = 'curvato.quickDraft';

const DEFAULT_FEATURE_PROFILE: CelebrationFeatureProfile = {
  quick_mode_enabled: true,
  module_flags: {
    venues: false,
    services: true,
    registry: false,
    budget: true,
    goals: true,
    guest_wall: false,
  },
  service_mode: 'order_food',
};

type QuickServiceMode = 'order_food' | 'hybrid';
type GuestAccessMode = 'open' | 'registration' | 'full';
type FoodChoice = 'order_food' | 'potluck' | 'hybrid';

const WIZARD_STEPS = [
  'Basics',
  'Date & Place',
  'Guests & RSVP',
  'Invite Style',
  'Food',
  'Quick Features',
  'Review & Create',
] as const;

// Predicted role for celebrant 1; additional celebrants use getPredictedRoleAt(type, index).
const getPredictedCelebrantRole = (type: string): string => getPredictedRoleAt(type, 0);

/** Pads additionalCelebrants to (min - 1) entries for the type, unless the user already edited the list. */
const padAdditionalCelebrantsForType = (
  type: string,
  current: CelebrantEntry[],
  touched: boolean,
): CelebrantEntry[] => {
  if (touched) return current;
  const minCount = getMinCelebrantsForType(type);
  if (minCount <= 1 || current.length >= minCount - 1) return current;
  const roleOptions = getCelebrantRoleOptions(type);
  const next = [...current];
  while (next.length < minCount - 1) {
    next.push({ name: '', role: roleOptions[next.length + 1] ?? roleOptions[roleOptions.length - 1] ?? 'Celebrant', email: '' });
  }
  return next;
};

const getEffectiveCelebrantEmail = ({
  hostEmail,
  celebrantEmail,
  isSurprise,
}: {
  hostEmail: string;
  celebrantEmail: string;
  isSurprise: boolean;
}): string => {
  const host = hostEmail.trim();
  const celebrant = celebrantEmail.trim();

  if (isSurprise) {
    return host || celebrant;
  }

  return celebrant || host;
};

const sanitizeFeatureProfile = (profile?: CelebrationFeatureProfile): CelebrationFeatureProfile => {
  const moduleFlags = {
    ...DEFAULT_FEATURE_PROFILE.module_flags,
    ...(profile?.module_flags ?? {}),
  };

  const rawMode = profile?.service_mode;
  const serviceMode: QuickServiceMode = rawMode === 'hybrid' ? 'hybrid' : 'order_food';

  return {
    quick_mode_enabled: true,
    module_flags: moduleFlags,
    service_mode: serviceMode,
  };
};

type ThemePreset = {
  id: string;
  label: string;
  config: LandingThemeConfig;
  gradientClass: string;
  swatchClasses: string[];
  headingClass: string;
  bodyClass: string;
};

const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'festive-warm',
    label: 'Festive Warm',
    config: {
      template_id: 'festive',
      palette_id: 'warm_default',
      font_pairing_id: 'playful',
    },
    gradientClass: 'bg-gradient-to-br from-orange-500 via-amber-400 to-rose-50',
    swatchClasses: ['bg-orange-500', 'bg-amber-400', 'bg-emerald-100', 'bg-rose-50'],
    headingClass: 'font-semibold',
    bodyClass: 'font-normal',
  },
  {
    id: 'garden-classic',
    label: 'Garden Classic',
    config: {
      template_id: 'elegant',
      palette_id: 'garden_party',
      font_pairing_id: 'classic',
    },
    gradientClass: 'bg-gradient-to-br from-emerald-600 via-rose-300 to-amber-50',
    swatchClasses: ['bg-emerald-600', 'bg-rose-300', 'bg-emerald-100', 'bg-amber-50'],
    headingClass: 'font-serif font-semibold',
    bodyClass: 'font-normal',
  },
  {
    id: 'modern-night',
    label: 'Modern Night',
    config: {
      template_id: 'modern',
      palette_id: 'night_sky',
      font_pairing_id: 'modern',
    },
    gradientClass: 'bg-gradient-to-br from-slate-900 via-indigo-700 to-slate-200',
    swatchClasses: ['bg-slate-900', 'bg-indigo-500', 'bg-amber-500', 'bg-slate-200'],
    headingClass: 'font-semibold tracking-tight',
    bodyClass: 'font-normal',
  },
  {
    id: 'minimal-clean',
    label: 'Minimal Clean',
    config: {
      template_id: 'minimal',
      palette_id: 'modern_minimal',
      font_pairing_id: 'formal',
    },
    gradientClass: 'bg-gradient-to-br from-zinc-900 via-zinc-500 to-zinc-100',
    swatchClasses: ['bg-zinc-900', 'bg-zinc-500', 'bg-zinc-200', 'bg-zinc-100'],
    headingClass: 'font-serif',
    bodyClass: 'font-normal',
  },
];

const toIsoDateTime = (date: string, time: string): string => {
  const local = new Date(`${date}T${time}:00`);
  return local.toISOString();
};

const isFutureIsoDateTime = (isoDateTime: string): boolean => {
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) {
    return false;
  }
  return parsed.getTime() > Date.now();
};

const getGuestLandingBaseUrl = (): string => {
  const configuredBaseUrl = import.meta.env.VITE_GUEST_LANDING_BASE_URL?.trim();
  if (configuredBaseUrl) {
    return configuredBaseUrl.replace(/\/$/, '');
  }

  const { protocol, hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:5437`;
  }

  return window.location.origin;
};

export const QuickEventCreate: React.FC = () => {
  const { t } = useTranslation('celebrations');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { templates: dbTemplates, loading: templatesLoading } = useCelebrationTemplates();
  const [title, setTitle] = useState('');
  const [celebrationType, setCelebrationType] = useState('birthday');
  const [celebrationSubtype, setCelebrationSubtype] = useState<string | null>(null);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>(null);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [eventDate, setEventDate] = useState(todayDate());
  const [startTime, setStartTime] = useState('19:00');
  const [endTime, setEndTime] = useState('22:00');
  const [locationName, setLocationName] = useState('');
  const [locationLat, setLocationLat] = useState<number | undefined>(undefined);
  const [locationLng, setLocationLng] = useState<number | undefined>(undefined);
  const [hostName, setHostName] = useState('');
  const [hostPhone, setHostPhone] = useState('');
  const [hostEmail, setHostEmail] = useState('');
  const [celebrantName, setCelebrantName] = useState('');
  const [celebrantRole, setCelebrantRole] = useState(getPredictedCelebrantRole('birthday'));
  const [celebrantRoleEdited, setCelebrantRoleEdited] = useState(false);
  const [celebrantEmail, setCelebrantEmail] = useState('');
  const [additionalCelebrants, setAdditionalCelebrants] = useState<CelebrantEntry[]>([]);
  const [additionalCelebrantsTouched, setAdditionalCelebrantsTouched] = useState(false);
  const [isSurprise, setIsSurprise] = useState(false);
  const [themePresetId, setThemePresetId] = useState(THEME_PRESETS[0].id);
  const [currentStep, setCurrentStep] = useState(0);
  const [guestAccessMode, setGuestAccessMode] = useState<GuestAccessMode>('open');
  const [foodChoice, setFoodChoice] = useState<FoodChoice>('order_food');
  const [selectedListKeys, setSelectedListKeys] = useState<string[]>([]);
  const [customListName, setCustomListName] = useState('');
  const [customListMode, setCustomListMode] = useState<'view' | 'signup' | 'contribute'>('signup');
  const [customLists, setCustomLists] = useState<Array<{ name: string; mode: 'view' | 'signup' | 'contribute' }>>([]);
  const [rsvpDeadline, setRsvpDeadline] = useState('');
  const [maxGuests, setMaxGuests] = useState('');
  const [plusOnesAllowed, setPlusOnesAllowed] = useState(false);
  const [plusOnesLimit, setPlusOnesLimit] = useState('1');
  const [guestListVisible, setGuestListVisible] = useState(false);
  const [featureProfile, setFeatureProfile] = useState<CelebrationFeatureProfile>(sanitizeFeatureProfile(DEFAULT_FEATURE_PROFILE));
  const [showIdentityGate, setShowIdentityGate] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<PublicQuickCreateCelebrationDto | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [result, setResult] = useState<QuickCreateCelebrationResponse | null>(null);
  const [draftId, setDraftId] = useState<string | undefined>(searchParams.get('draft') ?? undefined);
  const [draftHydrationComplete, setDraftHydrationComplete] = useState(!user?.id);
  const [useLocalDraftFallback, setUseLocalDraftFallback] = useState(!user?.id);
  const draftPatchChainRef = useRef(Promise.resolve());
  const draftCulturalHintsRef = useRef<string[]>([]);
  const [isReadinessLoading, setIsReadinessLoading] = useState(false);
  const [canShareInvite, setCanShareInvite] = useState(false);
  const [shareBlockingReasons, setShareBlockingReasons] = useState<string[]>([]);
  const guestLandingBaseUrl = getGuestLandingBaseUrl();

  useEffect(() => {
    if (!user?.id) {
      setDraftHydrationComplete(true);
      setUseLocalDraftFallback(true);
      return;
    }

    setDraftHydrationComplete(false);

    let cancelled = false;
    void (async () => {
      try {
        const serverDraft = draftId
          ? await celebrationService.getDraft(draftId)
          : await celebrationService.createDraft({ mode: 'quick', resume_state: { current_step: 0 } });
        if (cancelled) return;

        setDraftId(serverDraft.draft_id);
        setUseLocalDraftFallback(Object.keys(serverDraft.draft).length === 0);
        if (!draftId) {
          const params = new URLSearchParams(window.location.search);
          params.set('draft', serverDraft.draft_id);
          navigate(`/quick?${params.toString()}`, { replace: true });
        }

        const draft = serverDraft.draft;
        draftCulturalHintsRef.current = [draft.religion, draft.culture].filter((v): v is string => typeof v === 'string');
        if (typeof draft.title === 'string') setTitle(draft.title);
        if (typeof draft.celebration_type === 'string') setCelebrationType(draft.celebration_type);
        if (typeof draft.celebration_subtype === 'string') setCelebrationSubtype(draft.celebration_subtype);
        if (typeof draft.template === 'string') setSelectedTemplateKey(draft.template);
        if (typeof draft.event_date === 'string') setEventDate(draft.event_date);
        if (typeof draft.start_time === 'string') setStartTime(draft.start_time);
        if (typeof draft.end_time === 'string') setEndTime(draft.end_time);
        if (typeof draft.location_name === 'string') setLocationName(draft.location_name);
        if (typeof draft.latitude === 'number') setLocationLat(draft.latitude);
        if (typeof draft.longitude === 'number') setLocationLng(draft.longitude);
        if (typeof draft.host_name === 'string') setHostName(draft.host_name);
        if (draft.celebrant_names?.[0]) setCelebrantName(draft.celebrant_names[0]);
        if (draft.celebrant_roles?.[0]) setCelebrantRole(draft.celebrant_roles[0]);
        if (draft.celebrant_emails?.[0]) setCelebrantEmail(draft.celebrant_emails[0]);
        if ((draft.celebrant_names?.length ?? 0) > 1) {
          const extraCount = draft.celebrant_names!.length - 1;
          setAdditionalCelebrants(
            Array.from({ length: extraCount }, (_, i) => ({
              name: draft.celebrant_names?.[i + 1] ?? '',
              role: draft.celebrant_roles?.[i + 1] ?? '',
              email: draft.celebrant_emails?.[i + 1] ?? '',
            })),
          );
          setAdditionalCelebrantsTouched(true);
        }
        if (typeof draft.is_surprise === 'boolean') setIsSurprise(draft.is_surprise);
        if (typeof draft.rsvp_deadline === 'string') setRsvpDeadline(draft.rsvp_deadline);
        if (typeof draft.max_guests === 'number') setMaxGuests(String(draft.max_guests));
        if (typeof draft.plus_ones_allowed === 'boolean') setPlusOnesAllowed(draft.plus_ones_allowed);
        if (typeof draft.plus_ones_limit === 'number') setPlusOnesLimit(String(draft.plus_ones_limit));
        if (typeof draft.guest_list_visible === 'boolean') setGuestListVisible(draft.guest_list_visible);
        if (draft.feature_profile) setFeatureProfile(sanitizeFeatureProfile(draft.feature_profile));
        if (typeof serverDraft.resume_state.current_step === 'number') setCurrentStep(serverDraft.resume_state.current_step);
      } catch {
        // Browser localStorage and cross-mode draft remain the fallback.
        if (!cancelled) setUseLocalDraftFallback(true);
      } finally {
        if (!cancelled) setDraftHydrationComplete(true);
      }
    })();

    return () => { cancelled = true; };
  }, [draftId, navigate, user?.id]);

  useEffect(() => {
    if (user?.email && !hostEmail.trim()) {
      setHostEmail(user.email);
    }

    if (user?.full_name && !hostName.trim()) {
      setHostName(user.full_name);
    }

    if (user?.full_name && !celebrantName.trim()) {
      setCelebrantName(user.full_name);
    }
  }, [user, hostEmail, hostName, celebrantName]);

  useEffect(() => {
    if (isSurprise && hostEmail.trim() && celebrantEmail.trim() !== hostEmail.trim()) {
      setCelebrantEmail(hostEmail.trim());
    }
  }, [isSurprise, hostEmail, celebrantEmail]);

  const selectedThemePreset = useMemo(
    () => THEME_PRESETS.find((preset) => preset.id === themePresetId) ?? THEME_PRESETS[0],
    [themePresetId],
  );

  const selectedTemplate = useMemo(
    () => TEMPLATES.find((template) => template.id === selectedThemePreset.config.template_id) ?? TEMPLATES[0],
    [selectedThemePreset],
  );

  const enabledModulesCount = useMemo(
    () => Object.values(featureProfile.module_flags).filter(Boolean).length,
    [featureProfile],
  );

  useEffect(() => {
    const typeParam = searchParams.get('type');
    const isFreshTypeRoute = Boolean(typeParam) && !draftId;

    if (isFreshTypeRoute) {
      window.localStorage.removeItem(QUICK_DRAFT_KEY);
      clearCrossModeDraft();
      return;
    }

    if (user?.id && (!draftHydrationComplete || !useLocalDraftFallback)) {
      return;
    }

    const storedDraft = window.localStorage.getItem(QUICK_DRAFT_KEY);
    if (!storedDraft) {
      return;
    }

    try {
      const parsedDraft = JSON.parse(storedDraft) as {
        title?: string;
        celebrationType?: string;
        celebrationSubtype?: string | null;
        selectedTemplateKey?: string | null;
        eventDate?: string;
        startTime?: string;
        endTime?: string;
        locationName?: string;
        locationLat?: number;
        locationLng?: number;
        hostName?: string;
        hostEmail?: string;
        celebrantName?: string;
        celebrantRole?: string;
        celebrantEmail?: string;
        additionalCelebrants?: CelebrantEntry[];
        isSurprise?: boolean;
        themePresetId?: string;
        showRsvpSettings?: boolean;
        rsvpDeadline?: string;
        maxGuests?: string;
        plusOnesAllowed?: boolean;
        plusOnesLimit?: string;
        guestListVisible?: boolean;
        featureProfile?: CelebrationFeatureProfile;
      };

      if (parsedDraft.title) setTitle(parsedDraft.title);
      if (parsedDraft.celebrationType) setCelebrationType(parsedDraft.celebrationType);
      if (typeof parsedDraft.celebrationSubtype === 'string') setCelebrationSubtype(parsedDraft.celebrationSubtype);
      if (typeof parsedDraft.selectedTemplateKey === 'string') setSelectedTemplateKey(parsedDraft.selectedTemplateKey);
      if (parsedDraft.eventDate) setEventDate(parsedDraft.eventDate);
      if (parsedDraft.startTime) setStartTime(parsedDraft.startTime);
      if (parsedDraft.endTime) setEndTime(parsedDraft.endTime);
      if (parsedDraft.locationName) setLocationName(parsedDraft.locationName);
      if (typeof parsedDraft.locationLat === 'number') setLocationLat(parsedDraft.locationLat);
      if (typeof parsedDraft.locationLng === 'number') setLocationLng(parsedDraft.locationLng);
      if (parsedDraft.hostName) setHostName(parsedDraft.hostName);
      if (parsedDraft.hostEmail) setHostEmail(parsedDraft.hostEmail);
      if (parsedDraft.celebrantName) setCelebrantName(parsedDraft.celebrantName);
      if (parsedDraft.celebrantRole) {
        setCelebrantRole(parsedDraft.celebrantRole);
        setCelebrantRoleEdited(true);
      }
      if (parsedDraft.celebrantEmail) setCelebrantEmail(parsedDraft.celebrantEmail);
      if (Array.isArray(parsedDraft.additionalCelebrants) && parsedDraft.additionalCelebrants.length > 0) {
        setAdditionalCelebrants(parsedDraft.additionalCelebrants);
        setAdditionalCelebrantsTouched(true);
      }
      if (typeof parsedDraft.isSurprise === 'boolean') setIsSurprise(parsedDraft.isSurprise);
      if (parsedDraft.themePresetId && THEME_PRESETS.some((preset) => preset.id === parsedDraft.themePresetId)) {
        setThemePresetId(parsedDraft.themePresetId);
      }
      if (typeof parsedDraft.rsvpDeadline === 'string') setRsvpDeadline(parsedDraft.rsvpDeadline);
      if (typeof parsedDraft.maxGuests === 'string') setMaxGuests(parsedDraft.maxGuests);
      if (typeof parsedDraft.plusOnesAllowed === 'boolean') setPlusOnesAllowed(parsedDraft.plusOnesAllowed);
      if (typeof parsedDraft.plusOnesLimit === 'string') setPlusOnesLimit(parsedDraft.plusOnesLimit);
      if (typeof parsedDraft.guestListVisible === 'boolean') setGuestListVisible(parsedDraft.guestListVisible);
      if (parsedDraft.featureProfile) setFeatureProfile(sanitizeFeatureProfile(parsedDraft.featureProfile));
    } catch {
      window.localStorage.removeItem(QUICK_DRAFT_KEY);
    }

    const crossModeDraft = readCrossModeDraft();
    if (!crossModeDraft) {
      return;
    }

    if (crossModeDraft.title) setTitle(crossModeDraft.title);
    if (crossModeDraft.celebration_type) setCelebrationType(crossModeDraft.celebration_type);
    if (crossModeDraft.celebration_subtype) setCelebrationSubtype(crossModeDraft.celebration_subtype);
    if (crossModeDraft.template) setSelectedTemplateKey(crossModeDraft.template);
    if (crossModeDraft.event_date) setEventDate(crossModeDraft.event_date);
    if (crossModeDraft.start_time) setStartTime(crossModeDraft.start_time);
    if (crossModeDraft.end_time) setEndTime(crossModeDraft.end_time);
    if (crossModeDraft.location_name) setLocationName(crossModeDraft.location_name);
    if (typeof crossModeDraft.latitude === 'number') setLocationLat(crossModeDraft.latitude);
    if (typeof crossModeDraft.longitude === 'number') setLocationLng(crossModeDraft.longitude);
    if (crossModeDraft.host_name) setHostName(crossModeDraft.host_name);
    if (Array.isArray(crossModeDraft.celebrant_names) && crossModeDraft.celebrant_names[0]) {
      setCelebrantName(crossModeDraft.celebrant_names[0]);
    }
    if (Array.isArray(crossModeDraft.celebrant_roles) && crossModeDraft.celebrant_roles[0]) {
      setCelebrantRole(crossModeDraft.celebrant_roles[0]);
      setCelebrantRoleEdited(true);
    }
    if (Array.isArray(crossModeDraft.celebrant_emails) && crossModeDraft.celebrant_emails[0]) {
      setCelebrantEmail(crossModeDraft.celebrant_emails[0]);
    }
    if ((crossModeDraft.celebrant_names?.length ?? 0) > 1) {
      const names = crossModeDraft.celebrant_names as string[];
      const roles = crossModeDraft.celebrant_roles as string[] | undefined;
      const emails = crossModeDraft.celebrant_emails as string[] | undefined;
      setAdditionalCelebrants(
        names.slice(1).map((name, i) => ({
          name: name ?? '',
          role: roles?.[i + 1] ?? '',
          email: emails?.[i + 1] ?? '',
        })),
      );
      setAdditionalCelebrantsTouched(true);
    }
    if (typeof crossModeDraft.is_surprise === 'boolean') {
      setIsSurprise(crossModeDraft.is_surprise);
    }
    if (crossModeDraft.rsvp_deadline) setRsvpDeadline(crossModeDraft.rsvp_deadline);
    if (typeof crossModeDraft.max_guests === 'number') setMaxGuests(String(crossModeDraft.max_guests));
    if (typeof crossModeDraft.plus_ones_allowed === 'boolean') setPlusOnesAllowed(crossModeDraft.plus_ones_allowed);
    if (typeof crossModeDraft.plus_ones_limit === 'number') setPlusOnesLimit(String(crossModeDraft.plus_ones_limit));
    if (typeof crossModeDraft.guest_list_visible === 'boolean') setGuestListVisible(crossModeDraft.guest_list_visible);
    if (crossModeDraft.feature_profile) setFeatureProfile(sanitizeFeatureProfile(crossModeDraft.feature_profile));
  }, [draftHydrationComplete, useLocalDraftFallback, user?.id]);

  useEffect(() => {
    const type = searchParams.get('type');
    const subtype = searchParams.get('subtype');
    if (!type || !getTypeByKey(type)) {
      return;
    }

    setCelebrationType(type);
    if (!celebrantRoleEdited) {
      setCelebrantRole(getPredictedCelebrantRole(type));
    }
    setAdditionalCelebrants((current) => padAdditionalCelebrantsForType(type, current, additionalCelebrantsTouched));
    const hasValidSubtype = Boolean(subtype && getSubtypesForType(type).some((entry) => entry.key === subtype));
    setCelebrationSubtype(hasValidSubtype ? subtype! : null);
    setSelectedTemplateKey(null);
    setShowTemplatePicker(searchParams.get('template') === 'true');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // URL-driven type/subtype: preselect the matching DB template once templates have loaded.
  useEffect(() => {
    const type = searchParams.get('type');
    const subtype = searchParams.get('subtype');
    if (templatesLoading || !type || !getTypeByKey(type)) {
      return;
    }

    const hasValidSubtype = Boolean(subtype && getSubtypesForType(type).some((entry) => entry.key === subtype));
    setSelectedTemplateKey(getBestMatchTemplate(dbTemplates, type, hasValidSubtype ? subtype! : undefined)?.key ?? null);
  }, [searchParams, templatesLoading, dbTemplates]);

  // Resumed drafts may carry type/subtype without a template key; apply the best match once.
  const draftTemplateAppliedRef = useRef(false);
  useEffect(() => {
    if (draftTemplateAppliedRef.current || !draftHydrationComplete || templatesLoading) return;
    draftTemplateAppliedRef.current = true;
    if (selectedTemplateKey || !celebrationType) return;
    const match = getBestMatchTemplate(dbTemplates, celebrationType, celebrationSubtype ?? undefined, draftCulturalHintsRef.current);
    if (match) setSelectedTemplateKey(match.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftHydrationComplete, templatesLoading, dbTemplates]);

  const toggleFeatureModule = (moduleKey: keyof CelebrationFeatureProfile['module_flags']) => {
    setFeatureProfile((current) => ({
      ...current,
      quick_mode_enabled: true,
      module_flags: {
        ...current.module_flags,
        [moduleKey]: !current.module_flags[moduleKey],
      },
    }));
  };

  const setServiceMode = (mode: QuickServiceMode) => {
    setFeatureProfile((current) => ({
      ...current,
      quick_mode_enabled: true,
      service_mode: mode,
    }));
  };

  const displayCelebrantNames = useMemo(() => {
    const names = [celebrantName, ...additionalCelebrants.map((c) => c.name)]
      .map((n) => n.trim())
      .filter(Boolean);
    return names.length > 0 ? names.join(' & ') : hostName.trim();
  }, [celebrantName, additionalCelebrants, hostName]);

  useEffect(() => {
    if (result) {
      return;
    }

    const draftPayload = {
      title,
      celebrationType,
      celebrationSubtype,
      selectedTemplateKey,
      eventDate,
      startTime,
      endTime,
      locationName,
      locationLat,
      locationLng,
      hostName,
      hostEmail,
      celebrantName,
      celebrantRole,
      celebrantEmail,
      additionalCelebrants,
      isSurprise,
      themePresetId,
      rsvpDeadline,
      maxGuests,
      plusOnesAllowed,
      plusOnesLimit,
      guestListVisible,
      featureProfile,
    };

    window.localStorage.setItem(QUICK_DRAFT_KEY, JSON.stringify(draftPayload));

    const combinedCelebrants = toCelebrantArrays([
      { name: celebrantName, role: celebrantRole, email: celebrantEmail },
      ...additionalCelebrants,
    ]);

    writeCrossModeDraft({
      title: title.trim() || undefined,
      celebration_type: celebrationType,
      celebration_subtype: celebrationSubtype ?? undefined,
      template: selectedTemplateKey ?? undefined,
      event_date: eventDate,
      start_time: startTime,
      end_time: endTime,
      location_name: locationName.trim() || undefined,
      latitude: locationLat,
      longitude: locationLng,
      host_name: hostName.trim() || undefined,
      celebrant_names: celebrantName.trim() ? combinedCelebrants.names : undefined,
      celebrant_roles: celebrantName.trim() ? combinedCelebrants.roles : undefined,
      celebrant_emails: celebrantName.trim() ? combinedCelebrants.emails : undefined,
      is_surprise: isSurprise,
      rsvp_deadline: rsvpDeadline || undefined,
      max_guests: maxGuests.trim() ? Number(maxGuests) : undefined,
      plus_ones_allowed: plusOnesAllowed,
      plus_ones_limit: plusOnesLimit.trim() ? Number(plusOnesLimit) : undefined,
      guest_list_visible: guestListVisible,
      feature_profile: featureProfile,
    });
  }, [
    title,
    celebrationType,
    celebrationSubtype,
    selectedTemplateKey,
    eventDate,
    startTime,
    endTime,
    locationName,
    locationLat,
    locationLng,
    hostName,
    hostEmail,
    celebrantName,
    celebrantRole,
    celebrantEmail,
    additionalCelebrants,
    isSurprise,
    themePresetId,
    rsvpDeadline,
    maxGuests,
    plusOnesAllowed,
    plusOnesLimit,
    guestListVisible,
    featureProfile,
    result,
  ]);

  useEffect(() => {
    if (!draftId || !user?.id || !draftHydrationComplete || result) return;
    const patchCombinedCelebrants = toCelebrantArrays([
      { name: celebrantName, role: celebrantRole, email: celebrantEmail },
      ...additionalCelebrants,
    ]);
    const draft: CelebrationDraftDto = {
      title: title.trim() || undefined,
      celebration_type: celebrationType,
      celebration_subtype: celebrationSubtype ?? undefined,
      template: selectedTemplateKey ?? undefined,
      event_date: eventDate,
      start_time: startTime,
      end_time: endTime,
      location_name: locationName.trim() || undefined,
      latitude: locationLat,
      longitude: locationLng,
      host_name: hostName.trim() || undefined,
      celebrant_names: celebrantName.trim() ? patchCombinedCelebrants.names : undefined,
      celebrant_roles: celebrantName.trim() ? patchCombinedCelebrants.roles : undefined,
      celebrant_emails: celebrantName.trim() ? patchCombinedCelebrants.emails : undefined,
      is_surprise: isSurprise,
      rsvp_deadline: rsvpDeadline || undefined,
      max_guests: maxGuests.trim() ? Number(maxGuests) : undefined,
      plus_ones_allowed: plusOnesAllowed,
      plus_ones_limit: plusOnesLimit.trim() ? Number(plusOnesLimit) : undefined,
      guest_list_visible: guestListVisible,
      feature_profile: featureProfile,
    };
    const timer = window.setTimeout(() => {
      draftPatchChainRef.current = draftPatchChainRef.current
        .catch(() => undefined)
        .then(() => celebrationService.patchDraft(draftId, {
          draft,
          mode: 'quick',
          resume_state: { current_step: currentStep },
        }))
        .then(() => undefined);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [
    celebrationSubtype,
    celebrationType,
    celebrantEmail,
    celebrantName,
    celebrantRole,
    additionalCelebrants,
    currentStep,
    draftId,
    draftHydrationComplete,
    endTime,
    eventDate,
    featureProfile,
    guestListVisible,
    hostName,
    isSurprise,
    locationLat,
    locationLng,
    locationName,
    maxGuests,
    plusOnesAllowed,
    plusOnesLimit,
    result,
    rsvpDeadline,
    selectedTemplateKey,
    startTime,
    title,
    user?.id,
  ]);

  const absoluteShareUrl = useMemo(() => {
    if (!result?.share_url) {
      return '';
    }

    return new URL(result.share_url, `${guestLandingBaseUrl}/`).toString();
  }, [guestLandingBaseUrl, result]);

  useEffect(() => {
    if (!result?.celebration?.id) {
      setCanShareInvite(false);
      setShareBlockingReasons([]);
      setIsReadinessLoading(false);
      return;
    }

    const fallbackReasons: string[] = [];

    if (result.event.status !== 'planning') {
      fallbackReasons.push('Promote this celebration to planning before sharing the invite link.');
    }

    const hasLocation = Boolean(result.celebration.location_name?.trim() || result.event.venue_name?.trim());
    if (!hasLocation) {
      fallbackReasons.push('Add a venue/location before sharing.');
    }

    const hasRsvpDeadline = Boolean(result.celebration.rsvp_deadline);
    if (!hasRsvpDeadline) {
      fallbackReasons.push('Set an RSVP deadline before sharing.');
    }

    const hasHostName = Boolean(hostName.trim());
    if (!hasHostName) {
      fallbackReasons.push('Add host name for invite clarity.');
    }

    let isCancelled = false;
    setIsReadinessLoading(true);

    void (async () => {
      try {
        const readiness: CelebrationPromotionReadiness = await celebrationService.getPromotionReadiness(result.celebration.id);
        if (isCancelled) {
          return;
        }

        if (readiness.canPromote) {
          setCanShareInvite(true);
          setShareBlockingReasons([]);
          return;
        }

        const readinessReasons = [
          ...readiness.eventPlanningBlockers.flatMap((blocker) => blocker.reasons),
          ...readiness.blockingTasks.map((task) => `Task pending: ${task.title}`),
        ];

        if (readinessReasons.length > 0) {
          setCanShareInvite(false);
          setShareBlockingReasons(readinessReasons.slice(0, 4));
          return;
        }

        setCanShareInvite(false);
        setShareBlockingReasons(fallbackReasons);
      } catch {
        if (isCancelled) {
          return;
        }

        const canShareByFallback = fallbackReasons.length === 0;
        setCanShareInvite(canShareByFallback);
        setShareBlockingReasons(canShareByFallback ? [] : fallbackReasons);
      } finally {
        if (!isCancelled) {
          setIsReadinessLoading(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [result, hostName]);

  const resetForm = () => {
    setTitle('');
    setCelebrationType('birthday');
    setCelebrationSubtype(null);
    setSelectedTemplateKey(null);
    setShowTypePicker(false);
    setShowTemplatePicker(false);
    setEventDate(todayDate());
    setStartTime('19:00');
    setEndTime('22:00');
    setLocationName('');
    setLocationLat(undefined);
    setLocationLng(undefined);
    setHostName('');
    setHostPhone('');
    setHostEmail('');
    setCelebrantName('');
    setCelebrantRole(getPredictedCelebrantRole('birthday'));
    setCelebrantRoleEdited(false);
    setCelebrantEmail('');
    setAdditionalCelebrants([]);
    setAdditionalCelebrantsTouched(false);
    setIsSurprise(false);
    setThemePresetId(THEME_PRESETS[0].id);
    setCurrentStep(0);
    setGuestAccessMode('open');
    setFoodChoice('order_food');
    setSelectedListKeys([]);
    setCustomListName('');
    setCustomListMode('signup');
    setCustomLists([]);
    setRsvpDeadline('');
    setMaxGuests('');
    setPlusOnesAllowed(false);
    setPlusOnesLimit('1');
    setGuestListVisible(false);
    setShowIdentityGate(false);
    setPendingPayload(null);
    setErrorMessage(null);
    setCopied(false);
    setResult(null);
    window.localStorage.removeItem(QUICK_DRAFT_KEY);
    clearCrossModeDraft();
  };

  // Best-effort: lists can also be added later from the celebration page.
  const createSelectedLists = async (eventId: number): Promise<void> => {
    const keys = foodChoice === 'potluck' ? ['potluck', ...selectedListKeys] : selectedListKeys;
    const templates = eventListConfig.EVENT_LIST_TEMPLATES.filter((t) => keys.includes(t.key));
    const requests = templates.map((t) =>
      eventService.createEventList(eventId, { name: t.name, template_key: t.key, participation_mode: t.participation_mode }),
    );
    for (const custom of customLists) {
      requests.push(eventService.createEventList(eventId, { name: custom.name, template_key: null, participation_mode: custom.mode }));
    }
    await Promise.allSettled(requests);
  };

  const addCustomList = (): void => {
    const name = customListName.trim();
    if (!name || customLists.some((c) => c.name.toLowerCase() === name.toLowerCase())) return;
    setCustomLists((prev) => [...prev, { name, mode: customListMode }]);
    setCustomListName('');
  };

  const submitQuickCreate = async (payload: PublicQuickCreateCelebrationDto): Promise<void> => {
    setSubmitting(true);
    try {
      const response = await celebrationService.quickCreatePublicCelebration(payload);
      setResult(response);
      void createSelectedLists(response.event.id);
      setShowIdentityGate(false);
      setPendingPayload(null);
      if (draftId) {
        void celebrationService.completeDraft(draftId).catch(() => undefined);
      }
      window.localStorage.removeItem(QUICK_DRAFT_KEY);
      clearCrossModeDraft();
    } catch (error) {
      console.error('Quick event create failed', error);
      const apiMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      const normalizedError = handleApiError(error);

      if (apiMessage?.includes('Host email or phone is required')) {
        setShowIdentityGate(true);
        setPendingPayload(payload);
      } else {
        setErrorMessage(normalizedError || apiMessage || 'Could not create event. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const copyShareLink = async (): Promise<void> => {
    if (!absoluteShareUrl) return;
    await navigator.clipboard.writeText(absoluteShareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareVia = (channel: 'whatsapp' | 'sms' | 'email') => {
    if (!absoluteShareUrl || !title) return;

    const message = encodeURIComponent(`Join ${title} on Curvato: ${absoluteShareUrl}`);
    const mailBody = encodeURIComponent(`You are invited to ${title}.\n\nOpen invite: ${absoluteShareUrl}`);

    if (channel === 'whatsapp') {
      window.open(`https://wa.me/?text=${message}`, '_blank', 'noopener,noreferrer');
      return;
    }

    if (channel === 'sms') {
      window.location.href = `sms:?body=${message}`;
      return;
    }

    window.location.href = `mailto:?subject=${encodeURIComponent(`You're invited: ${title}`)}&body=${mailBody}`;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);

    if (!title.trim()) {
      setErrorMessage('Event title is required.');
      return;
    }

    if (endTime <= startTime) {
      setErrorMessage('End time must be after start time.');
      return;
    }

    const parsedMaxGuests = maxGuests.trim() ? Number(maxGuests) : undefined;
    if (parsedMaxGuests !== undefined && (!Number.isFinite(parsedMaxGuests) || parsedMaxGuests <= 0)) {
      setErrorMessage('Guest cap must be a positive number.');
      return;
    }

    const parsedPlusOnesLimit = plusOnesAllowed && plusOnesLimit.trim() ? Number(plusOnesLimit) : undefined;
    if (parsedPlusOnesLimit !== undefined && (!Number.isFinite(parsedPlusOnesLimit) || parsedPlusOnesLimit < 1)) {
      setErrorMessage('+1 limit must be at least 1 when enabled.');
      return;
    }

    const startDateTimeIso = toIsoDateTime(eventDate, startTime);
    if (!isFutureIsoDateTime(startDateTimeIso)) {
      setErrorMessage('Event date must be in the future.');
      return;
    }

    const normalizedCelebrantName = celebrantName.trim() || hostName.trim();
    const normalizedCelebrantEmail = getEffectiveCelebrantEmail({
      hostEmail: hostEmail.trim(),
      celebrantEmail: celebrantEmail.trim(),
      isSurprise,
    });
    if (!normalizedCelebrantName) {
      setErrorMessage('At least one celebrant name is required.');
      return;
    }

    const submitCombinedCelebrants = toCelebrantArrays([
      { name: normalizedCelebrantName, role: celebrantRole, email: normalizedCelebrantEmail },
      ...additionalCelebrants,
    ]);

    const payload: PublicQuickCreateCelebrationDto = {
      title: title.trim(),
      celebration_type: celebrationType,
      celebration_subtype: celebrationSubtype ?? undefined,
      template_key: selectedTemplateKey ?? undefined,
      event_title: title.trim(),
      event_type: 'other',
      event_date: eventDate,
      start_time: startDateTimeIso,
      end_time: toIsoDateTime(eventDate, endTime),
      location_name: locationName.trim() || undefined,
      host_name: hostName.trim() || undefined,
      host_email: hostEmail.trim() || undefined,
      host_phone: hostPhone.trim() || undefined,
      celebrant_names: submitCombinedCelebrants.names,
      celebrant_roles: submitCombinedCelebrants.roles,
      celebrant_emails: submitCombinedCelebrants.emails.some(Boolean) ? submitCombinedCelebrants.emails : undefined,
      is_surprise: isSurprise,
      landing_theme: selectedThemePreset.config,
      rsvp_deadline: rsvpDeadline ? `${rsvpDeadline}T23:59:59.000Z` : undefined,
      max_guests: parsedMaxGuests,
      plus_ones_allowed: plusOnesAllowed,
      plus_ones_limit: plusOnesAllowed ? parsedPlusOnesLimit : undefined,
      guest_list_visible: guestListVisible,
      feature_profile: featureProfile,
    };

    setPendingPayload(payload);
    await submitQuickCreate(payload);
  };

  const validateStep = (step: number): string | null => {
    if (step === 0) {
      if (!title.trim()) return 'Event title is required.';
      if (!celebrantName.trim() && !hostName.trim()) return 'At least one celebrant name is required.';
    }

    if (step === 1) {
      if (!eventDate) return 'Event date is required.';
      if (!startTime || !endTime) return 'Start and end times are required.';
      if (endTime <= startTime) return 'End time must be after start time.';
      if (!isFutureIsoDateTime(toIsoDateTime(eventDate, startTime))) return 'Event date must be in the future.';
    }

    return null;
  };

  const advanceStep = () => {
    const validationError = validateStep(currentStep);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setErrorMessage(null);
    setCurrentStep((step) => Math.min(step + 1, WIZARD_STEPS.length - 1));
  };

  const skipStep = () => {
    setErrorMessage(null);
    setCurrentStep((step) => Math.min(step + 1, WIZARD_STEPS.length - 1));
  };

  const handleIdentityGateSubmit = async () => {
    setErrorMessage(null);

    if (!pendingPayload) {
      setErrorMessage('Please complete event details first.');
      return;
    }

    const email = hostEmail.trim();
    const phone = hostPhone.trim();

    if (!email && !phone) {
      setErrorMessage('Enter an email or phone to receive your manage link.');
      return;
    }

    await submitQuickCreate({
      ...pendingPayload,
      host_email: email || undefined,
      host_phone: phone || undefined,
    });
  };

  const switchToChat = () => {
    writeCrossModeDraft({
      title: title.trim() || undefined,
      celebration_type: celebrationType,
      celebration_subtype: celebrationSubtype ?? undefined,
      template: selectedTemplateKey ?? undefined,
      event_date: eventDate,
      start_time: startTime,
      end_time: endTime,
      location_name: locationName.trim() || undefined,
      latitude: locationLat,
      longitude: locationLng,
      host_name: hostName.trim() || undefined,
      rsvp_deadline: rsvpDeadline || undefined,
      max_guests: maxGuests.trim() ? Number(maxGuests) : undefined,
      plus_ones_allowed: plusOnesAllowed,
      plus_ones_limit: plusOnesLimit.trim() ? Number(plusOnesLimit) : undefined,
      guest_list_visible: guestListVisible,
      feature_profile: featureProfile,
    });

    const draftQuery = draftId ? `&draft=${encodeURIComponent(draftId)}` : '';
    navigate(`/celebrations/new?mode=chat${draftQuery}`);
  };

  const canShareInviteLink = Boolean(result?.share_url && result.event.status === 'planning' && canShareInvite);

  if (result) {
    return (
      <PageContainer width="default" className="space-y-4">
        <AppBreadcrumbs />
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <PartyPopper className="h-6 w-6 text-primary" />
              {t('quick.shareTitle', { title: result.celebration?.title || title || 'Your event' })}
            </CardTitle>
            <CardDescription>
              {isReadinessLoading
                ? 'Checking invite readiness...'
                : canShareInvite
                  ? 'Share your invite link now and start collecting RSVPs.'
                  : 'Complete setup requirements before sharing the invite.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">Next steps</h3>
              <ol className="space-y-2">
                <li className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-background/70 p-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">1. Add your guests</p>
                    <p className="text-xs text-muted-foreground">Build the guest list before inviting anyone.</p>
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => navigate(`/celebrations/${result.celebration.id}/guests`)}>Guests</Button>
                </li>
                <li className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-background/70 p-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">2. Complete event planning</p>
                    <p className="text-xs text-muted-foreground">Add activities and any remaining event details to move out of draft mode.</p>
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => navigate(`/celebrations/${result.celebration.id}`)}>Open celebration</Button>
                </li>
                <li className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-background/70 p-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">3. Set your RSVP deadline</p>
                    <p className="text-xs text-muted-foreground">Choose when guests should respond before you share the invite.</p>
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => navigate(`/celebrations/${result.celebration.id}/guest-landing`)}>RSVP settings</Button>
                </li>
              </ol>
            </div>

            {!isReadinessLoading && !canShareInvite && shareBlockingReasons.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <p className="font-medium">Invite sharing is temporarily locked.</p>
                <ul className="mt-1 list-disc pl-5">
                  {shareBlockingReasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.event.status !== 'planning' && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Invite sharing is available only after the celebration is in planning mode.
              </div>
            )}

            {result.event.status === 'planning' && (
              <div className="space-y-2 rounded-lg border border-border/60 bg-muted/20 p-3">
                <h3 className="text-xs font-medium text-muted-foreground">Share your invite link</h3>
                <div className="rounded-lg border bg-white p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-sm text-primary font-medium">{absoluteShareUrl}</p>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={copyShareLink} disabled={!canShareInviteLink || isReadinessLoading}>
                        <Copy className="h-3.5 w-3.5 mr-1" />
                        {copied ? 'Copied' : 'Copy'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(absoluteShareUrl, '_blank', 'noopener,noreferrer')}
                        disabled={!canShareInviteLink || isReadinessLoading}
                      >
                        <ExternalLink className="h-3.5 w-3.5 mr-1" />
                        Open
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => shareVia('whatsapp')} disabled={!canShareInviteLink || isReadinessLoading}>
                    <MessageCircle className="h-3.5 w-3.5 mr-1" />
                    WhatsApp
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => shareVia('sms')} disabled={!canShareInviteLink || isReadinessLoading}>
                    <Smartphone className="h-3.5 w-3.5 mr-1" />
                    SMS
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => shareVia('email')} disabled={!canShareInviteLink || isReadinessLoading}>
                    <Mail className="h-3.5 w-3.5 mr-1" />
                    Email
                  </Button>
                </div>
              </div>
            )}

            {result.host_identity_sent_to && (
              <div className="rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary">
                {t('quick.shareSent', { contact: result.host_identity_sent_to })}
              </div>
            )}

            <div className="pt-1">
              <Button variant="ghost" onClick={resetForm}>
                <RotateCcw className="h-4 w-4 mr-1" />
                Create another quick event
              </Button>
            </div>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer width="default" className="space-y-4">
      <AppBreadcrumbs />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Sparkles className="h-6 w-6 text-primary" />
            {title.trim() || 'Create an Event in 60 Seconds'}
          </CardTitle>
          <CardDescription>
            Build your celebration in a few focused steps. You can skip optional choices and adjust them later.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <nav aria-label="Celebration creation progress" className="space-y-2">
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>Step {currentStep + 1} of {WIZARD_STEPS.length}</span>
                <span className="font-medium text-foreground">{WIZARD_STEPS[currentStep]}</span>
              </div>
              <div className="grid grid-cols-7 gap-1" aria-hidden="true">
                {WIZARD_STEPS.map((step, index) => (
                  <div key={step} className={`h-1.5 rounded-full ${index <= currentStep ? 'bg-primary' : 'bg-muted'}`} />
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 text-[10px] text-muted-foreground">
                {WIZARD_STEPS.map((step, index) => (
                  <span key={step} className={`whitespace-nowrap ${index === currentStep ? 'font-semibold text-foreground' : ''}`}>{step}</span>
                ))}
              </div>
            </nav>

            <div className={`grid gap-5 ${currentStep === WIZARD_STEPS.length - 1 ? '' : 'lg:grid-cols-[1.15fr_0.85fr]'}`}>
              <div className="space-y-4">
                <div className={`space-y-1.5 ${currentStep !== 0 ? 'hidden' : ''}`}>
                <Label htmlFor="quick-title">Title*</Label>
                <Input
                  id="quick-title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Riya's 30th Birthday"
                  required
                />
              </div>

                <div className={`space-y-3 rounded-lg border border-border/80 bg-muted/20 p-3 ${currentStep !== 0 ? 'hidden' : ''}`}>
                  <button
                    type="button"
                    onClick={() => setShowTypePicker((current) => !current)}
                    className="flex w-full items-center justify-between text-left"
                    aria-expanded={showTypePicker}
                  >
                    <span className="text-sm font-medium text-foreground">Celebration type: {getLabelForType(celebrationType)}</span>
                    <span className="text-xs text-muted-foreground">{showTypePicker ? 'Hide' : 'Change'}</span>
                  </button>
                  {showTypePicker && (
                    <TypeGalleryPicker
                      selectedType={celebrationType}
                      onSelect={(type) => {
                        setCelebrationType(type);
                        if (!celebrantRoleEdited) {
                          setCelebrantRole(getPredictedCelebrantRole(type));
                        }
                        setAdditionalCelebrants((current) => padAdditionalCelebrantsForType(type, current, additionalCelebrantsTouched));
                        setCelebrationSubtype(null);
                        setSelectedTemplateKey(getBestMatchTemplate(dbTemplates, type)?.key ?? null);
                        setShowTypePicker(false);
                      }}
                    />
                  )}
                  <SubtypePicker
                    celebrationType={celebrationType}
                    selectedSubtype={celebrationSubtype}
                    onSelect={(subtype) => {
                      setCelebrationSubtype(subtype);
                      setSelectedTemplateKey(getBestMatchTemplate(dbTemplates, celebrationType, subtype ?? undefined)?.key ?? null);
                    }}
                  />
                </div>

                <div className={`rounded-lg border border-border/80 bg-muted/20 p-3 ${currentStep !== 0 ? 'hidden' : ''}`}>
                  <button
                    type="button"
                    onClick={() => setShowTemplatePicker((current) => !current)}
                    className="flex w-full items-center justify-between text-left"
                    aria-expanded={showTemplatePicker}
                  >
                    <span className="text-sm font-medium text-foreground">Planning template</span>
                    <span className="text-xs text-muted-foreground">{showTemplatePicker ? 'Hide' : 'Choose'}</span>
                  </button>
                  {showTemplatePicker && (
                    <div className="mt-3">
                      <TemplatePicker
                        celebrationType={celebrationType}
                        celebrationSubtype={celebrationSubtype}
                        selectedTemplateKey={selectedTemplateKey}
                        onSelect={setSelectedTemplateKey}
                      />
                    </div>
                  )}
                </div>

                <div className={`space-y-1.5 ${currentStep !== 0 ? 'hidden' : ''}`}>
                <Label htmlFor="quick-host">Host name</Label>
                <Input
                  id="quick-host"
                  value={hostName}
                  onChange={(event) => setHostName(event.target.value)}
                  placeholder="Riya & Arjun"
                />
              </div>

                <div className={`space-y-1.5 ${currentStep !== 0 ? 'hidden' : ''}`}>
                <Label htmlFor="quick-host-email">Host email (optional)</Label>
                <Input
                  id="quick-host-email"
                  type="email"
                  value={hostEmail}
                  onChange={(event) => setHostEmail(event.target.value)}
                  placeholder="host@example.com"
                />
              </div>

                <div className={`space-y-1.5 ${currentStep !== 0 ? 'hidden' : ''}`}>
                <CelebrantsEditor
                  celebrationType={celebrationType}
                  celebrants={[{ name: celebrantName, role: celebrantRole, email: celebrantEmail }, ...additionalCelebrants]}
                  onChange={(next) => {
                    const [first, ...rest] = next;
                    setCelebrantName(first?.name ?? '');
                    setCelebrantRole(first?.role ?? '');
                    setCelebrantRoleEdited(true);
                    setCelebrantEmail(first?.email ?? '');
                    setAdditionalCelebrants(rest);
                    setAdditionalCelebrantsTouched(true);
                  }}
                  isSurprise={isSurprise}
                  hostEmail={hostEmail}
                  idPrefix="quick-celebrant"
                />
              </div>

              <label htmlFor="quick-celebrant-surprise" className={`flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-sm ${currentStep !== 0 ? 'hidden' : ''}`}>
                <input
                  id="quick-celebrant-surprise"
                  type="checkbox"
                  checked={isSurprise}
                  onChange={(event) => setIsSurprise(event.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border text-primary"
                />
                <span>
                  <span className="font-medium text-foreground">This is a surprise for the celebrant</span>
                  <span className="block text-xs text-muted-foreground">The celebrant will not be treated as an event organizer.</span>
                </span>
              </label>

                <div className="space-y-1.5" hidden={currentStep !== 1}>
                <Label htmlFor="quick-date" className="inline-flex items-center gap-1">
                  <CalendarDays className="h-3.5 w-3.5" />
                  When*
                </Label>
                <Input
                  id="quick-date"
                  type="date"
                  value={eventDate}
                  onChange={(event) => setEventDate(event.target.value)}
                  required
                />
              </div>

                <div className="grid grid-cols-2 gap-2" hidden={currentStep !== 1}>
                <div className="space-y-1.5" hidden={currentStep !== 1}>
                  <Label htmlFor="quick-start" className="inline-flex items-center gap-1">
                    <Clock3 className="h-3.5 w-3.5" />
                    Start*
                  </Label>
                  <Input
                    id="quick-start"
                    type="time"
                    value={startTime}
                    onChange={(event) => setStartTime(event.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1.5" hidden={currentStep !== 1}>
                  <Label htmlFor="quick-end">End*</Label>
                  <Input
                    id="quick-end"
                    type="time"
                    value={endTime}
                    onChange={(event) => setEndTime(event.target.value)}
                    required
                  />
                </div>
              </div>

                <div className="space-y-1.5" hidden={currentStep !== 1}>
                <Label htmlFor="quick-location" className="inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  Where
                </Label>
                <LocationAutocomplete
                  value={locationName}
                  onInputValueChange={(value) => {
                    setLocationName(value);
                    setLocationLat(undefined);
                    setLocationLng(undefined);
                  }}
                  onLocationSelect={(location) => {
                    setLocationName(location.name);
                    setLocationLat(location.lat);
                    setLocationLng(location.lng);
                  }}
                  placeholder="Sky Lounge, Baner"
                  className="mt-1"
                  inputClassName="h-9 text-sm"
                />
              </div>

                <div className="space-y-2" hidden={currentStep !== 3}>
                  <Label>Invite vibe</Label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {THEME_PRESETS.map((preset) => {
                      const active = preset.id === themePresetId;

                      return (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => setThemePresetId(preset.id)}
                          className={`rounded-lg border px-3 py-2 text-left transition ${active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
                        >
                          <div className="text-sm font-semibold text-foreground">{preset.label}</div>
                          <div className="mt-2 flex items-center gap-1.5">
                            {preset.swatchClasses.slice(0, 4).map((swatchClass) => (
                              <span
                                key={`${preset.id}-${swatchClass}`}
                                className={`h-3.5 w-3.5 rounded-full border border-white/60 ${swatchClass}`}
                              />
                            ))}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-4 rounded-xl border border-border/80 bg-white/60 p-1 shadow-sm">
                    <div className={`rounded-lg p-5 ${selectedThemePreset.gradientClass}`}>
                      <div className="rounded-lg border border-white/30 bg-white/90 p-5 text-center shadow-sm backdrop-blur-sm">
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-600">You're invited</p>
                        <h3 className={`mt-3 text-2xl text-slate-900 ${selectedThemePreset.headingClass}`}>
                          {title.trim() || 'Your celebration title'}
                        </h3>
                        <p className={`mt-2 text-sm text-slate-700 ${selectedThemePreset.bodyClass}`}>
                          Celebrating {displayCelebrantNames || 'someone special'}
                        </p>
                        <div className="my-4 border-t border-slate-300" />
                        <div className={`space-y-2 text-sm text-slate-700 ${selectedThemePreset.bodyClass}`}>
                          <p><span className="font-medium">When:</span> {eventDate || todayDate()} at {startTime}</p>
                          <p><span className="font-medium">Where:</span> {locationName.trim() || 'Venue to be confirmed'}</p>
                          <p><span className="font-medium">Hosted by:</span> {hostName.trim() || 'Host name'}</p>
                        </div>
                        <div className="mt-5 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-slate-50">
                          RSVP
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-border/80 bg-muted/20 p-3" hidden={currentStep !== 2}>
                  <div className="space-y-2">
                    <Label>Guest access</Label>
                    <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Guest access">
                      {([
                        ['open', 'Open invite link', 'Best for quick, low-friction sharing. Guests can view the event and respond from a single link.'],
                        ['registration', 'Registration link', 'Collect guest details and RSVP responses while still keeping the flow simple for hosts.'],
                        ['full', 'Full functionality', 'Use the full guest management experience with details, quotas, and richer invite workflows.'],
                      ] as const).map(([value, label, description]) => (
                        <label
                          key={value}
                          className={`flex cursor-pointer flex-col gap-2 rounded-md border px-3 py-3 text-sm transition ${guestAccessMode === value ? 'border-primary bg-primary/5' : 'border-border bg-background hover:border-primary/50'}`}
                        >
                          <span className="flex items-center gap-2 font-medium text-foreground">
                            <input
                              type="radio"
                              name="quick-guest-access"
                              checked={guestAccessMode === value}
                              onChange={() => setGuestAccessMode(value)}
                            />
                            {label}
                          </span>
                          <span className="text-xs text-muted-foreground">{description}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">This choice guides setup only. Guest management happens after creation.</p>
                  </div>

                  {guestAccessMode !== 'full' && guestAccessMode !== 'open' && (
                    <div className="mt-3 space-y-3">
                      <span className="text-sm font-medium text-foreground">RSVP settings</span>
                      <div className="space-y-1.5">
                        <Label htmlFor="quick-rsvp-deadline">RSVP deadline</Label>
                        <Input
                          id="quick-rsvp-deadline"
                          type="date"
                          value={rsvpDeadline}
                          onChange={(event) => setRsvpDeadline(event.target.value)}
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="quick-guest-cap">Guest cap</Label>
                        <Input
                          id="quick-guest-cap"
                          type="number"
                          min={1}
                          inputMode="numeric"
                          value={maxGuests}
                          onChange={(event) => setMaxGuests(event.target.value)}
                          placeholder="Optional"
                        />
                      </div>

                      <label className="flex items-center gap-2 text-sm text-foreground">
                        <input
                          type="checkbox"
                          checked={plusOnesAllowed}
                          onChange={(event) => setPlusOnesAllowed(event.target.checked)}
                        />
                        Allow +1s
                      </label>

                      {plusOnesAllowed && (
                        <div className="space-y-1.5">
                          <Label htmlFor="quick-plus-one-limit">+1 max per guest</Label>
                          <Input
                            id="quick-plus-one-limit"
                            type="number"
                            min={1}
                            inputMode="numeric"
                            value={plusOnesLimit}
                            onChange={(event) => setPlusOnesLimit(event.target.value)}
                          />
                        </div>
                      )}

                      <label className="flex items-center gap-2 text-sm text-foreground">
                        <input
                          type="checkbox"
                          checked={guestListVisible}
                          onChange={(event) => setGuestListVisible(event.target.checked)}
                        />
                        Show attendee names on invite page
                      </label>
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-border/80 bg-muted/20 p-3" hidden={currentStep !== 5}>
                  <div className="flex items-center justify-between gap-3 text-left">
                    <span className="text-sm font-medium text-foreground">Quick features</span>
                    <span className="text-xs text-muted-foreground">{enabledModulesCount} enabled</span>
                  </div>

                  <div className="mt-3 space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Pick what to enable now. You can add more modules later from the celebration page.
                    </p>

                    <div className="grid gap-2 sm:grid-cols-2">
                      {([
                        ['venues', 'Venue planning', 'Keep venue ideas, rooms, and logistics in one place.'],
                        ['services', 'Services', 'Track vendors, requests, and booking progress.'],
                        ['registry', 'Gift registry', 'Collect wish lists and gifting details for guests.'],
                        ['budget', 'Budget', 'Monitor estimated and actual costs for the event.'],
                        ['goals', 'Goals and tasks', 'Break the celebration into milestones and to-dos.'],
                        ['guest_wall', 'Guest wall', 'Display shared memories, updates, and guest interactions.'],
                      ] as const).map(([moduleKey, label, description]) => (
                        <label
                          key={moduleKey}
                          className="flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2 text-sm text-foreground"
                        >
                          <span className="flex-1">
                            <span className="block font-medium">{label}</span>
                            <span className="mt-0.5 block text-[11px] text-muted-foreground">{description}</span>
                          </span>
                          <input
                            type="checkbox"
                            checked={Boolean(featureProfile.module_flags[moduleKey])}
                            onChange={() => toggleFeatureModule(moduleKey)}
                          />
                        </label>
                      ))}
                    </div>

                  </div>
                </div>

                <div className="rounded-lg border border-border/80 bg-muted/20 p-3" hidden={currentStep !== 4}>
                    <div className="space-y-1.5">
                      <Label>Food coordination mode</Label>
                      <p className="text-xs text-muted-foreground">Choose a starting point for food planning. You can add menus, dietary needs, and vendors after creation.</p>
                      <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Food coordination mode">
                        <label
                          className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition ${foodChoice === 'order_food' ? 'border-primary bg-primary/5' : 'border-border bg-background hover:border-primary/50'}`}
                        >
                          <input
                            type="radio"
                            name="quick-food-mode"
                            checked={foodChoice === 'order_food'}
                            onChange={() => {
                              setFoodChoice('order_food');
                              setServiceMode('order_food');
                            }}
                          />
                          <span>Order food</span>
                        </label>
                        <label
                          className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition ${foodChoice === 'potluck' ? 'border-primary bg-primary/5' : 'border-border bg-background hover:border-primary/50'}`}
                        >
                          <input
                            type="radio"
                            name="quick-food-mode"
                            checked={foodChoice === 'potluck'}
                            onChange={() => setFoodChoice('potluck')}
                          />
                          <span>Potluck</span>
                        </label>
                        <label
                          className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition ${foodChoice === 'hybrid' ? 'border-primary bg-primary/5' : 'border-border bg-background hover:border-primary/50'}`}
                        >
                          <input
                            type="radio"
                            name="quick-food-mode"
                            checked={foodChoice === 'hybrid'}
                            onChange={() => {
                              setFoodChoice('hybrid');
                              setServiceMode('hybrid');
                            }}
                          />
                          <span>Hybrid</span>
                        </label>
                      </div>
                      <div className="mt-3 space-y-2">
                        <Label>Lists (optional)</Label>
                        <p className="text-xs text-muted-foreground">Add sign-up lists for guests, such as drinks or a DJ playlist.</p>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {eventListConfig.EVENT_LIST_TEMPLATES.filter((template) => template.key !== 'potluck').map((template) => (
                            <label key={template.key} className="flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm">
                              <input
                                type="checkbox"
                                checked={selectedListKeys.includes(template.key)}
                                onChange={() =>
                                  setSelectedListKeys((prev) =>
                                    prev.includes(template.key) ? prev.filter((k) => k !== template.key) : [...prev, template.key],
                                  )
                                }
                              />
                              <span>{template.name}</span>
                            </label>
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Input
                            className="min-w-[12rem] flex-1"
                            value={customListName}
                            onChange={(event) => setCustomListName(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                addCustomList();
                              }
                            }}
                            placeholder="Custom list name"
                            maxLength={100}
                          />
                          <select
                            className="rounded-md border bg-background px-2 text-sm"
                            value={customListMode}
                            onChange={(event) => setCustomListMode(event.target.value as 'view' | 'signup' | 'contribute')}
                            aria-label="Custom list participation"
                          >
                            <option value="signup">Sign-up</option>
                            <option value="contribute">Guests add entries</option>
                            <option value="view">View only</option>
                          </select>
                          <Button type="button" variant="secondary" size="sm" onClick={addCustomList} disabled={!customListName.trim()}>Add</Button>
                        </div>
                        {customLists.length > 0 && (
                          <ul className="space-y-1">
                            {customLists.map((custom) => (
                              <li key={custom.name} className="flex items-center justify-between rounded-md border bg-background px-3 py-1.5 text-sm">
                                <span>{custom.name} <span className="text-xs text-muted-foreground">({custom.mode === 'signup' ? 'Sign-up' : custom.mode === 'contribute' ? 'Guests add entries' : 'View only'})</span></span>
                                <button
                                  type="button"
                                  className="text-xs text-muted-foreground hover:text-foreground"
                                  onClick={() => setCustomLists((prev) => prev.filter((c) => c.name !== custom.name))}
                                >
                                  Remove
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Food coordination is managed separately so you can add more planning modules without changing the event setup flow.
                      </p>
                    </div>
                </div>
              </div>

              <div className="space-y-3" hidden={currentStep !== 6}>
                <div className="rounded-lg border border-border/80 bg-muted/20 p-4">
                  <h3 className="font-semibold text-foreground">Review your celebration</h3>
                  <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                    <div className="rounded-md bg-background p-3"><dt className="text-xs text-muted-foreground">Title</dt><dd className="mt-1 font-medium text-foreground">{title.trim() || 'Not set'}</dd></div>
                    <div className="rounded-md bg-background p-3"><dt className="text-xs text-muted-foreground">Type</dt><dd className="mt-1 font-medium text-foreground">{getLabelForType(celebrationType)}</dd></div>
                    <div className="rounded-md bg-background p-3"><dt className="text-xs text-muted-foreground">Date & time</dt><dd className="mt-1 font-medium text-foreground">{eventDate} at {startTime} - {endTime}</dd></div>
                    <div className="rounded-md bg-background p-3"><dt className="text-xs text-muted-foreground">Place</dt><dd className="mt-1 font-medium text-foreground">{locationName.trim() || 'Venue to be confirmed'}</dd></div>
                    <div className="rounded-md bg-background p-3"><dt className="text-xs text-muted-foreground">Host</dt><dd className="mt-1 font-medium text-foreground">{hostName.trim() || 'Not set'}</dd></div>
                    <div className="rounded-md bg-background p-3"><dt className="text-xs text-muted-foreground">Celebrant</dt><dd className="mt-1 font-medium text-foreground">{displayCelebrantNames || 'Not set'}</dd></div>
                    <div className="rounded-md bg-background p-3"><dt className="text-xs text-muted-foreground">Guest access</dt><dd className="mt-1 font-medium text-foreground">{guestAccessMode === 'open' ? 'Open invite link' : guestAccessMode === 'registration' ? 'Registration link' : 'Full functionality'}</dd></div>
                    <div className="rounded-md bg-background p-3"><dt className="text-xs text-muted-foreground">Food</dt><dd className="mt-1 font-medium text-foreground">{foodChoice === 'potluck' ? 'Potluck' : foodChoice === 'hybrid' ? 'Hybrid' : 'Order food'}</dd></div>
                  </dl>
                  <div className="mt-3 rounded-md border border-border/70 bg-background p-3 text-sm">
                    <p className="text-xs text-muted-foreground">Enabled features</p>
                    <p className="mt-1 font-medium text-foreground">
                      {Object.entries(featureProfile.module_flags).filter(([, enabled]) => enabled).map(([module]) => module.replace(/_/g, ' ')).join(', ') || 'None selected'}
                    </p>
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">Creating the celebration does not add guests or send invitations. Manage guests after creation.</p>
                </div>

                <div className="rounded-xl border border-border/80 bg-white/60 p-1 shadow-sm">
                  <div className={`rounded-lg p-5 ${selectedThemePreset.gradientClass}`}>
                    <div className="rounded-lg border border-white/30 bg-white/90 p-5 text-center shadow-sm backdrop-blur-sm">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-600">You're invited</p>
                      <h3 className={`mt-3 text-2xl text-slate-900 ${selectedThemePreset.headingClass}`}>
                        {title.trim() || 'Your celebration title'}
                      </h3>
                      <p className={`mt-2 text-sm text-slate-700 ${selectedThemePreset.bodyClass}`}>
                        Celebrating {displayCelebrantNames || 'someone special'}
                      </p>
                      <div className="my-4 border-t border-slate-300" />
                      <div className={`space-y-2 text-sm text-slate-700 ${selectedThemePreset.bodyClass}`}>
                        <p><span className="font-medium">When:</span> {eventDate || todayDate()} at {startTime} - {endTime}</p>
                        <p><span className="font-medium">Where:</span> {locationName.trim() || 'Venue to be confirmed'}</p>
                        <p><span className="font-medium">Hosted by:</span> {hostName.trim() || 'Host name'}</p>
                        <p className="text-xs text-slate-500">Template: {selectedTemplate.name}</p>
                      </div>
                      <div className="mt-5 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-slate-50">
                        RSVP
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">
                  Theme and layout are saved to this event and shown on your public invite page.
                </p>
              </div>
            </div>

            {errorMessage && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {errorMessage}
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
              <div className="flex flex-wrap gap-2">
                {currentStep > 0 && (
                  <Button type="button" variant="outline" onClick={() => { setErrorMessage(null); setCurrentStep((step) => step - 1); }}>
                    Back
                  </Button>
                )}
                {currentStep < WIZARD_STEPS.length - 1 ? (
                  <>
                    {currentStep > 1 && (
                      <Button type="button" variant="ghost" onClick={skipStep}>
                        Skip
                      </Button>
                    )}
                    <Button type="button" onClick={advanceStep}>
                      Next
                    </Button>
                  </>
                ) : (
                  <Button type="submit" disabled={submitting}>
                    <LinkIcon className="h-4 w-4 mr-1" />
                    {submitting ? t('form.buttons.creating') : 'Create'}
                  </Button>
                )}
              </div>
              <Button type="button" variant="ghost" onClick={switchToChat}>
                Switch to AI chat
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {showIdentityGate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>{t('quick.gateTitle')}</CardTitle>
              <CardDescription>
                {t('quick.gateSubtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="gate-email">Email</Label>
                <Input
                  id="gate-email"
                  type="email"
                  value={hostEmail}
                  onChange={(event) => setHostEmail(event.target.value)}
                  placeholder="host@curvato.us"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="gate-phone">Phone</Label>
                <Input
                  id="gate-phone"
                  value={hostPhone}
                  onChange={(event) => setHostPhone(event.target.value)}
                  placeholder="+1 415 555 0100"
                />
              </div>

              <p className="text-xs text-muted-foreground">
                {t('quick.gateReassure')}
              </p>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowIdentityGate(false)}
                  disabled={submitting}
                >
                  Back
                </Button>
                <Button type="button" onClick={handleIdentityGateSubmit} disabled={submitting}>
                  {submitting ? t('form.buttons.creating') : t('quick.gateCta')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </PageContainer>
  );
};
